import { useState } from "react";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ViewsDropdown } from "@/components/views/ViewsDropdown";
import type { SavedViewRead } from "@/api/types";

interface TaskViewSelectorProps {
  views: SavedViewRead[];
  loading: boolean;
  activeView: SavedViewRead | null;
  onSelect: (view: SavedViewRead) => void;
  onCreate: (name: string) => Promise<void>;
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  /** Toggle the view's default flag (set or remove). */
  onToggleDefault: (view: SavedViewRead) => Promise<void>;
  onSaveActive: () => Promise<void>;
  /** Revert the live filters back to the active view's saved blob. */
  onReset: () => void;
  /** True when the live filters differ from the active view's saved blob. */
  hasUnsavedChanges: boolean;
  /** Gates create / save / rename / delete / set-default (views.create). */
  canManage: boolean;
}

/**
 * Saved-views control for the Tasks page. The picker itself reuses the
 * companies `ViewsDropdown` (searchable dropdown + per-row ⋯ menu +
 * "Create new view"); alongside it sit "Save" / "Reset" affordances that
 * appear when the active view has unsaved filter changes.
 */
export function TaskViewSelector({
  views,
  loading,
  activeView,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onToggleDefault,
  onSaveActive,
  onReset,
  hasUnsavedChanges,
  canManage,
}: TaskViewSelectorProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingActive, setSavingActive] = useState(false);

  async function submitCreate() {
    const name = nameInput.trim();
    if (!name) return;
    setBusy(true);
    try {
      await onCreate(name);
      setCreateOpen(false);
      setNameInput("");
    } catch {
      toast.error("Couldn't save the view");
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveActive() {
    setSavingActive(true);
    try {
      await onSaveActive();
    } catch {
      toast.error("Couldn't update the view");
    } finally {
      setSavingActive(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-slate-500">Views:</span>

      <ViewsDropdown
        views={views}
        loading={loading}
        activeView={activeView}
        canCreate={canManage}
        canShare={false}
        onPickView={onSelect}
        onRename={(view, name) => onRename(view.id, name)}
        onToggleShared={async () => {}}
        onToggleDefault={onToggleDefault}
        onDelete={(view) => onDelete(view.id)}
        onCreateNewClick={() => {
          setNameInput("");
          setCreateOpen(true);
        }}
      />

      {hasUnsavedChanges && activeView && (
        <>
          {canManage && (
            <button
              type="button"
              onClick={() => void handleSaveActive()}
              disabled={savingActive}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-700"
            >
              {savingActive ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </button>
          )}
          {/* Discard unsaved edits — revert to the active view's blob. */}
          <button
            type="button"
            onClick={onReset}
            disabled={savingActive}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </>
      )}

      {/* Create dialog — opened from the dropdown's "Create new view". */}
      <Dialog
        open={createOpen}
        onOpenChange={(o) => {
          if (busy) return;
          if (!o) setCreateOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Save current view</DialogTitle>
            <DialogDescription>
              Save the current filters and view mode as a named view.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            placeholder="View name…"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submitCreate();
              }
            }}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCreateOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void submitCreate()}
              disabled={busy || nameInput.trim() === ""}
            >
              {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
